import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import {
    portfolioSpeciesSummaryQuery,
    portfolioSpeciesDetailQuery,
    PortfolioSpeciesDetail,
    PortfolioSpeciesDetailQueryResult,
    portfolioRedListSpeciesQuery,
    portfolioInvasiveSpeciesQuery,
    PortfolioSpeciesSummary,
    PortfolioSpeciesListResponse,
    PortfolioSpeciesSummaryQueryResult,
    PortfolioRedListSpeciesQueryResult,
    PortfolioInvasiveSpeciesQueryResult,
} from './portfolioSpeciesQuery';

type SummaryQueryParams = {
    customerId: string;
    filters?: { name: string; values: string[] }[];
};

type DetailQueryParams = SummaryQueryParams & {
    scientificName: string;
};

type ListQueryParams = SummaryQueryParams & {
    pagination: { page: number; pageSize: number };
    sortBy?: { field: string; order: 'ASC' | 'DESC' };
    /**
     * Narrows the list server-side to species whose scientific or common name
     * contains it. Part of the args, so it is part of RTK's cache key and two
     * searches never share a page. Absent, never '': an empty term is no filter,
     * and sending one would key a second entry on the unfiltered list.
     */
    searchText?: string;
};

/**
 * Sort fields the grid can order by. Go rejects anything else rather than
 * silently falling back, so a column that maps to nothing here would surface an
 * error instead of an arbitrary order — which is why `predictionCreatedAt` has no
 * entry and the new table has no "Added" column.
 */
export const PORTFOLIO_SPECIES_SORT_FIELDS = ['scientificName', 'commonName', 'siteCount', 'iucnCategory'] as const;

const portfolioSpeciesGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        portfolioSpeciesSummary: builder.query<PortfolioSpeciesSummary, SummaryQueryParams>({
            query: params => ({
                body: portfolioSpeciesSummaryQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioSpeciesSummaryQueryResult) => response.portfolio.getPortfolioSpeciesSummary,
        }),
        portfolioSpeciesDetail: builder.query<PortfolioSpeciesDetail, DetailQueryParams>({
            query: params => ({
                body: portfolioSpeciesDetailQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioSpeciesDetailQueryResult) => response.portfolio.getPortfolioSpeciesDetail,
        }),
        portfolioRedListSpecies: builder.query<PortfolioSpeciesListResponse, ListQueryParams>({
            query: params => ({
                body: portfolioRedListSpeciesQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioRedListSpeciesQueryResult) => response.portfolio.getPortfolioRedListSpecies,
        }),
        portfolioInvasiveSpecies: builder.query<PortfolioSpeciesListResponse, ListQueryParams>({
            query: params => ({
                body: portfolioInvasiveSpeciesQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioInvasiveSpeciesQueryResult) => response.portfolio.getPortfolioInvasiveSpecies,
        }),
    }),
});

export const {
    usePortfolioSpeciesSummaryQuery,
    usePortfolioRedListSpeciesQuery,
    usePortfolioInvasiveSpeciesQuery,
    usePortfolioSpeciesDetailQuery,
} = portfolioSpeciesGraphSlice;

export default portfolioSpeciesGraphSlice.reducer;
